import { Link } from "react-router-dom";
import { Seo } from "../../components/Seo";
import { gymLocalBusiness } from "../../components/JsonLd";
import { useGyms, type Gym } from "../../api/hooks";
import { InstagramSection } from "../../components/InstagramFeed";
import { Reveal } from "../../components/Reveal";
import { MobileCarousel } from "../../components/MobileCarousel";
import { gymMapUrl } from "../../lib/maps";
import { track } from "../../lib/track";

const EXPERTISE_ITEMS = [
  {
    title: "Coaching Personnel",
    desc: "Programmation individualisée, adaptée à vos objectifs, vos pathologies et vos disponibilités.",
  },
  {
    title: "Préparation Physique",
    desc: "Pour sportifs et équipes, format 1h15, sur mesure possible.",
  },
  {
    title: "Héraclès Kids",
    desc: "Le sport dès le plus jeune âge.",
  },
  {
    title: "Entreprises",
    desc: "Cohésion d'équipe et préparation physique, sur mesure.",
  },
];

export function Home() {
  const { data: gyms } = useGyms();

  const jsonLd = gyms?.map(gymLocalBusiness) ?? [];
  const gymCities = gyms?.map((g) => g.name.replace(/^Héraclès( II)? /, "")) ?? [];
  const gymsHeadline =
    gymCities.length === 2
      ? "Deux box. Une même énergie."
      : gyms && gyms.length > 0
        ? `${gyms.length === 1 ? "Une box" : `${numberToWord(gyms.length)} box`} dans le Finistère`
        : "Nos box dans le Finistère";
  const gymsSubtitle =
    gymCities.length === 2
      ? `${gymCities[0]} ou ${gymCities[1]}, choisissez la box qui s'intègre le mieux à votre quotidien.`
      : "Choisissez la box qui s'intègre le mieux à votre quotidien.";

  return (
    <>
      <Seo
        title="CrossFit Héraclès — Brest & Landerneau"
        description="Box CrossFit à Brest et Landerneau. Cours collectifs, coaching personnel, CrossFit Kids. Séance d'essai 100% offerte."
        path="/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative flex min-h-[90vh] flex-col overflow-hidden bg-ink-900 text-white">
        {/* Image de fond — déposer le fichier dans apps/web/public/hero.jpg */}
        <img
          src="/hero.jpg"
          alt=""
          className="animate-hero-zoom absolute inset-0 h-full w-full object-cover object-[center_60%]"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-ink-900/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 via-transparent to-ink-900/20" />

        <div className="container relative flex flex-1 flex-col justify-center py-24">
          <span className="eyebrow animate-fade-up">Héraclès</span>
          <h1 className="h-hero animate-fade-up mt-4" style={{ animationDelay: "120ms" }}>
            Devenez la meilleure<br />version de vous-même.
          </h1>
          <p className="animate-fade-up mt-6 max-w-xl text-lg text-ink-300 sm:text-xl" style={{ animationDelay: "240ms" }}>
            Une communauté, des coachs et des entraînements pour progresser durablement.
            Séance d'essai <strong className="text-white">100% offerte</strong>.
          </p>
          <span className="animate-fade-up mt-6 text-sm font-semibold uppercase tracking-[0.2em] text-white/70" style={{ animationDelay: "300ms" }}>
            {gyms?.map((g) => g.name.replace(/^Héraclès( II)? /, "")).join(" · ") || "Brest · Landerneau"}
          </span>
          <div className="animate-fade-up mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap" style={{ animationDelay: "360ms" }}>
            <Link to="/contact?type=trial" className="btn-primary">
              Réserver ma séance d'essai
            </Link>
            {gyms?.[0] && (
              <Link to={`/plannings/${gyms[0].slug}`} className="btn-outline border-white/20 text-white">
                Voir les plannings
              </Link>
            )}
          </div>
        </div>

        <div className="container relative border-t border-white/15 py-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 sm:text-sm">
            Plus qu'une salle de sport. Une exigence de qualité et de professionnalisme.
          </p>
        </div>
      </section>

      {/* Notre expertise CrossFit */}
      <section className="section">
        <div className="container">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Notre expertise</span>
            <h2 className="h-section mt-2">CrossFit</h2>
            <p className="mt-2 text-base text-ink-500 sm:text-lg">
              L'entraînement au cœur d'Héraclès.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-center">
            <Reveal className="overflow-hidden rounded-lg">
              <img
                src="/services.jpg"
                alt="Coach briefant un groupe chez Héraclès"
                className="h-72 w-full object-cover sm:h-96"
                loading="lazy"
              />
            </Reveal>
            <Reveal delay={120}>
              <p className="text-base text-ink-700 sm:text-lg">
                10 formats différents, en groupes de 10 à 20 personnes, encadrés par nos
                coachs. Adaptées à tous les niveaux, pour développer force, endurance,
                mobilité, coordination et condition physique.
              </p>
              <Link to="/equipe" className="btn-primary mt-6 inline-flex">
                Découvrir notre équipe
              </Link>
            </Reveal>
          </div>

          {/* Desktop / tablette : grille */}
          <div className="mt-10 hidden gap-x-6 gap-y-8 lg:grid lg:grid-cols-4">
            {EXPERTISE_ITEMS.map((s, i) => (
              <Reveal key={s.title} delay={i * 80} className="border-t-2 border-brand-500 pt-4">
                <h3 className="font-display text-lg font-bold uppercase">{s.title}</h3>
                <p className="mt-2 text-sm text-ink-500">{s.desc}</p>
              </Reveal>
            ))}
          </div>

          {/* Mobile : carousel horizontal */}
          <div className="mt-10 lg:hidden">
            <MobileCarousel
              items={EXPERTISE_ITEMS}
              keyFn={(s) => s.title}
              topPad={false}
              renderItem={(s) => (
                <div className="border-t-2 border-brand-500 pt-4">
                  <h3 className="font-display text-lg font-bold uppercase">{s.title}</h3>
                  <p className="mt-2 text-sm text-ink-500">{s.desc}</p>
                </div>
              )}
            />
          </div>
        </div>
      </section>

      {/* Nos implantations */}
      <section className="section bg-white">
        <div className="container">
          <Reveal className="max-w-2xl">
            <span className="eyebrow">Nos implantations</span>
            <h2 className="h-section mt-2">{gymsHeadline}</h2>
            <p className="mt-3 text-base text-ink-500 sm:text-lg">{gymsSubtitle}</p>
          </Reveal>

          {/* Desktop / tablette : grille */}
          <div className="mt-10 hidden gap-10 lg:grid lg:grid-cols-2">
            {gyms?.map((gym, i) => (
              <Reveal key={gym.id} delay={i * 120}>
                <GymShowcaseCard
                  gym={gym}
                  image={gym.imageUrl || (i === 0 ? "/heracles1.jpg" : "/heracles2.jpg")}
                />
              </Reveal>
            ))}
          </div>

          {/* Mobile : carousel horizontal */}
          <div className="mt-10 lg:hidden">
            <MobileCarousel
              items={gyms ?? []}
              keyFn={(gym) => gym.id}
              topPad={false}
              renderItem={(gym, i) => (
                <GymShowcaseCard
                  gym={gym}
                  image={gym.imageUrl || (i === 0 ? "/heracles1.jpg" : "/heracles2.jpg")}
                />
              )}
            />
          </div>
        </div>
      </section>

      {/* Instagram */}
      <InstagramSection gyms={gyms} />

      {/* CTA */}
      <section className="bg-brand-500 py-16 text-white">
        <Reveal className="container flex flex-col items-center gap-4 text-center">
          <h2 className="h-section">Prêt à essayer ?</h2>
          <p className="max-w-xl">
            Une séance d'essai offerte, sans engagement. Vous venez, on s'occupe du reste.
          </p>
          <Link to="/contact?type=trial" className="btn bg-white text-brand-600 hover:bg-ink-100">
            Réserver ma séance d'essai
          </Link>
        </Reveal>
      </section>
    </>
  );
}

function GymShowcaseCard({ gym, image }: { gym: Gym; image?: string }) {
  return (
    <article className="card group overflow-hidden p-0">
      {image && (
        <div className="overflow-hidden">
          <img
            src={image}
            alt={gym.name}
            className="aspect-[3/1] w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-6">
        <h3 className="font-display text-xl font-bold uppercase">{gym.name}</h3>
        <a
          href={gymMapUrl(gym)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track({ type: "directions_click", gym: gym.slug })}
          className="mt-2 flex items-start gap-1.5 text-sm text-ink-500 hover:text-brand-500"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
            className="mt-0.5 shrink-0 text-brand-500"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {gym.address}
        </a>
        <div className="mt-4">
          <Link to={`/contact?gym=${gym.slug}&type=trial`} className="btn-primary w-full">
            Réserver ma séance d'essai
          </Link>
        </div>
      </div>
    </article>
  );
}

function numberToWord(n: number) {
  const words = ["zéro", "une", "deux", "trois", "quatre", "cinq", "six"];
  return words[n] ?? String(n);
}
